// src/pages/BarcodeReader/BarcodeReaderPage.tsx
// Clean, Google-inspired Barcode Scanner with SEO optimization

import React, { useState, useRef, ChangeEvent } from 'react';
import SEO from '../../../components/SEO/SEO';
import { ToolSchema, BreadcrumbSchema, HowToSchema } from '../../../components/Schema';
import { seoConfig } from '../../../config/seo';
import {
    CheckCircle, Shield, Zap, Scan,
    Upload, Copy, Check, Camera, QrCode, RefreshCw
} from 'lucide-react';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../../components/ErrorMessage/ErrorMessage';
import apiService from '../../../services/apiService';
import {
    ToolPageLayout,
    ToolInfoStrip,
    ToolSection,
    ToolStepCard,
    ToolFeatureCard,
    ToolFaqCta,
    ToolRelatedCard,
} from '../components';
import SecurityBadge from '../../../components/Shared/SecurityBadge/SecurityBadge';
import { toast } from 'react-toastify';
import tokenManager from '../../../utils/tokenManager';

interface BarcodeResult {
    type: string;
    data: string;
}

const BarcodeReaderPage: React.FC = () => {
    const [isReading, setIsReading] = useState<boolean>(false);
    const [readResults, setReadResults] = useState<BarcodeResult[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                toast.error('File size exceeds 10MB limit');
                return;
            }
            setSelectedFile(file);
            setReadResults([]);
            setError(null);
        }
    };

    const handleReadBarcode = async () => {
        if (!selectedFile) {
            toast.error('Please select an image containing a barcode or QR code');
            return;
        }

        setIsReading(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            const securityHeaders = await tokenManager.getSecurityHeader() || {};
            const response = await fetch(`${apiService.baseURL}/api/tools/qrcode/read`, {
                method: 'POST',
                headers: securityHeaders as HeadersInit,
                body: formData
            });

            if (!response.ok) {
                throw new Error(`Server responded with ${response.status}`);
            }

            const data = await response.json();

            if (data.error) {
                setError(data.error);
                toast.error(data.error);
                setReadResults([]);
            } else if (data.results && data.results.length > 0) {
                setReadResults(data.results);
                toast.success(`Found ${data.results.length} barcode(s)!`);
            } else {
                setError('No barcodes or QR codes found in the image');
                toast.warn('No barcodes detected. Try a clearer image.');
                setReadResults([]);
            }
        } catch (err: any) {
            console.error(err);
            setError(`Failed to read barcode: ${err.message}`);
            toast.error('Scan failed. See details below.');
            setReadResults([]);
        } finally {
            setIsReading(false);
        }
    };

    const copyToClipboard = (text: string, index: number) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedIndex(index);
            toast.success('Barcode data copied!');
            setTimeout(() => setCopiedIndex(null), 2000);
        }).catch(err => {
            console.error('Failed to copy:', err);
            toast.error('Failed to copy to clipboard');
        });
    };

    const handleReset = () => {
        setSelectedFile(null);
        setReadResults([]);
        setError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        toast.info('Scanner reset');
    };

    return (
        <>
            <SEO {...seoConfig.barcode} />
            <ToolSchema
                name="Barcode & QR Scanner"
                description="Free online barcode and QR code scanner. Scan and decode multiple barcode formats including QR, Code 128, EAN, UPC using your camera or uploaded images."
                url="/tools/barcode-scanner"
                applicationCategory="UtilitiesApplication"
                featureList={[
                    "Scan QR codes and barcodes",
                    "Upload image for scanning",
                    "Multiple format support (QR, EAN, UPC, Code 128)",
                    "Instant decode results",
                    "Copy or export decoded data",
                    "Works on mobile devices"
                ]}
            />
            <BreadcrumbSchema
                breadcrumbs={[
                    { name: 'Home', url: 'https://www.filefusion.app/' },
                    { name: 'Tools', url: 'https://www.filefusion.app/tools' },
                    { name: 'Barcode Scanner', url: 'https://www.filefusion.app/tools/barcode-scanner' }
                ]}
            />
            <HowToSchema
                name="How to Scan Barcodes and QR Codes Online"
                description="Scan and decode barcodes and QR codes from images using our free online tool."
                totalTime="PT1M"
                steps={[
                    { name: "Upload Image", text: "Click or drag and drop an image containing a barcode or QR code." },
                    { name: "Scan Image", text: "Click the scan button to decode all barcodes in the image." },
                    { name: "View Results", text: "See decoded data with barcode type and information." },
                    { name: "Copy Data", text: "Click copy to save the decoded information to your clipboard." }
                ]}
                tool={["Web Browser", "Image with barcode"]}
            />

            <ToolPageLayout
                icon={<Scan size={32} strokeWidth={1.5} />}
                title="Free Barcode &amp; QR Scanner"
                subtitle="Scan and decode QR codes, barcodes, and product codes from images. Supports all major formats including QR, Code 128, EAN, UPC, and more. No app download required."
                tags={(
                    <>
                        <SecurityBadge type="secure" />
                        <SecurityBadge type="deleted" />
                        <SecurityBadge type="private" />
                    </>
                )}
            >
                <div className="flex-1 min-w-0">
                    {error && (
                        <ErrorMessage
                            type="unknown"
                            message={error}
                            onClose={() => setError(null)}
                        />
                    )}

                    <div className="mb-[48px]">
                        <div className="bg-[#ffffff] border-[1px] border-[#dadce0] rounded-[12px] p-[24px] md:p-[32px] shadow-[0_1px_3px_0_rgba(60,64,67,0.3),0_4px_8px_3px_rgba(60,64,67,0.15)]">
                            <div>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-[12px] sm:gap-[20px] mb-[28px] pb-[20px] border-b-[1px] border-[#dadce0]">
                                    <div className="stage-number">1</div>
                                    <div className="stage-info">
                                        <h2 className="text-[1.375rem] font-[400] text-[#202124] m-0 mb-[8px] text-center">Upload Image to Scan</h2>
                                        <p className="text-[0.9375rem] text-[#5f6368] m-0 mb-[24px] text-center leading-[1.6]">Choose an image containing barcodes or QR codes</p>
                                    </div>
                                </div>

                                <div
                                    className="border-[2px] border-dashed border-[#dadce0] rounded-[12px] py-[40px] px-[20px] bg-[#f8f9fa] cursor-pointer transition-all duration-200 min-h-[240px] md:min-h-[280px] flex flex-col items-center justify-center hover:border-[#1a73e8] hover:bg-[#e8f0fe]"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    {selectedFile ? (
                                        <div className="flex flex-col items-center gap-[16px] w-full">
                                            <img
                                                src={URL.createObjectURL(selectedFile)}
                                                alt="Selected"
                                                className="max-w-full max-h-[200px] rounded-[8px] object-contain"
                                            />
                                            <div className="flex items-center gap-[8px] text-[#1e8e3e] text-[0.9375rem] font-medium">
                                                <CheckCircle size={20} />
                                                <span>{selectedFile.name}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center text-center gap-[12px] [&>svg]:text-[#80868b] [&>svg]:mb-[8px] [&>p]:text-[1rem] [&>p]:text-[#202124] [&>p]:m-0 [&>span]:text-[0.875rem] [&>span]:text-[#5f6368]">
                                            <Upload size={48} />
                                            <p>Click to upload image</p>
                                            <span>or drag and drop</span>
                                            <span className="!block mt-[8px] !font-[0.8125rem] !text-[#80868b]">JPG, PNG, GIF, WebP (Max 10MB)</span>
                                        </div>
                                    )}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileSelect}
                                        style={{ display: 'none' }}
                                    />
                                </div>

                                <div className="flex flex-col md:flex-row gap-[12px] mt-[24px] justify-center">
                                    <button
                                        className={`inline-flex items-center justify-center gap-[8px] py-[12px] px-[24px] text-[0.9375rem] font-medium rounded-[4px] cursor-pointer transition-all duration-200 font-inherit w-full md:w-auto ${(!selectedFile || isReading) ? 'bg-[#dadce0] text-[#80868b] cursor-not-allowed' : 'bg-[#1a73e8] text-white border-none hover:bg-[#1557b0] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]'}`}
                                        onClick={handleReadBarcode}
                                        disabled={!selectedFile || isReading}
                                    >
                                        {isReading ? (
                                            <>
                                                <LoadingSpinner size="small" />
                                                Scanning...
                                            </>
                                        ) : (
                                            <>
                                                <Scan size={20} />
                                                Scan Barcode
                                            </>
                                        )}
                                    </button>
                                    {selectedFile && (
                                        <button className="inline-flex items-center justify-center gap-[8px] py-[12px] px-[24px] text-[0.9375rem] font-medium rounded-[4px] cursor-pointer transition-all duration-200 font-inherit w-full md:w-auto bg-[#ffffff] text-[#5f6368] border-[1px] border-[#dadce0] hover:bg-[#f8f9fa]" onClick={handleReset}>
                                            <RefreshCw size={18} />
                                            Reset
                                        </button>
                                    )}
                                </div>
                            </div>

                            {readResults.length > 0 && (
                                <div className="mt-[32px] pt-[32px] border-t-[1px] border-[#dadce0]">
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-[12px] sm:gap-[20px] mb-[28px] pb-[20px] border-b-[1px] border-[#dadce0]">
                                        <div className="stage-number">2</div>
                                        <div className="stage-info">
                                            <h3 className="text-[1.125rem] font-medium text-[#202124] m-0 mb-[8px]">Scan Results ({readResults.length} found)</h3>
                                            <p className="text-[0.9375rem] text-[#5f6368] m-0">Copy decoded information below</p>
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-[12px]">
                                        {readResults.map((result, index) => (
                                            <div key={index} className="p-[16px] bg-[#f8f9fa] border-[1px] border-[#dadce0] rounded-[12px]">
                                                <div className="flex justify-between items-center mb-[12px]">
                                                    <span className="inline-block py-[4px] px-[10px] bg-[#e8f0fe] text-[#1a73e8] text-[0.75rem] font-semibold rounded-[12px] uppercase">{result.type}</span>
                                                    <button
                                                        className="inline-flex items-center gap-[6px] py-[6px] px-[12px] bg-[#ffffff] border-[1px] border-[#dadce0] rounded-[4px] text-[0.8125rem] font-medium text-[#5f6368] cursor-pointer transition-all duration-200 font-inherit hover:bg-[#e8f0fe] hover:text-[#1a73e8]"
                                                        onClick={() => copyToClipboard(result.data, index)}
                                                    >
                                                        {copiedIndex === index ? (
                                                            <>
                                                                <Check size={16} />
                                                                Copied!
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Copy size={16} />
                                                                Copy
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                                <div className="text-[0.9375rem] text-[#202124] leading-[1.6] break-all font-mono p-[12px] bg-[#ffffff] rounded-[4px]">{result.data}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </ToolPageLayout>

            <ToolInfoStrip>
                <ToolInfoStrip.Section variant="privacy">
                    <h3><Shield size={20} />Privacy First</h3>
                    <p>Decoding happens securely. Images are processed in real-time and automatically purged. We never store barcode data or original images.</p>
                </ToolInfoStrip.Section>
                <ToolInfoStrip.Section>
                    <h3><Zap size={20} />Pro Tips</h3>
                    <p>For best results, make sure the barcode image is well-lit and not too blurry. High-resolution photos from your phone work perfectly.</p>
                </ToolInfoStrip.Section>
                <ToolInfoStrip.Section>
                    <h3><CheckCircle size={20} />Supported Formats</h3>
                    <ul>
                        <li>QR Codes &amp; Data Matrix</li>
                        <li>EAN-13 &amp; EAN-8 (Products)</li>
                        <li>UPC-A &amp; UPC-E (US Products)</li>
                    </ul>
                </ToolInfoStrip.Section>
            </ToolInfoStrip>

            <ToolSection variant="light" title="How to Scan Barcodes in 3 Steps" grid="steps">
                <ToolStepCard number={1} title="Upload Image" description="Click or drag and drop an image containing one or more barcodes. Supports photos from your phone, screenshots, or scanned documents up to 10MB." />
                <ToolStepCard number={2} title="Click Scan" description="Click the scan button and our tool will automatically detect and decode all barcodes and QR codes found in your image using advanced algorithms." />
                <ToolStepCard number={3} title="View & Copy Results" description="See decoded data with barcode type identification. Click copy to save information to clipboard or use it in other applications." />
            </ToolSection>

            <ToolSection variant="muted" title="Powerful Barcode Scanning Features" description="Our scanner uses advanced image processing to detect and decode multiple barcode formats with high accuracy, even from low-quality images." grid="features">
                <ToolFeatureCard><Scan size={28} /><h3>Multiple Formats</h3><p>Supports all major barcode formats: QR codes, Code 128, Code 39, EAN-13, EAN-8, UPC-A, UPC-E, ITF, Data Matrix, and more. One tool for all your scanning needs.</p></ToolFeatureCard>
                <ToolFeatureCard><Camera size={28} /><h3>Image Upload</h3><p>Upload images from your device containing barcodes. Works with photos, screenshots, and scanned documents. Supports JPG, PNG, GIF, and WebP formats.</p></ToolFeatureCard>
                <ToolFeatureCard><Zap size={28} /><h3>Instant Decoding</h3><p>Get barcode data in seconds. Our advanced algorithms process images quickly and accurately, even handling multiple codes in a single image.</p></ToolFeatureCard>
                <ToolFeatureCard><CheckCircle size={28} /><h3>High Accuracy</h3><p>Advanced image preprocessing handles blurry images, poor lighting, and various angles. Multiple detection techniques ensure reliable scanning results.</p></ToolFeatureCard>
            </ToolSection>

            <ToolSection variant="muted" title="When to Use Barcode Scanner" grid="features">
                <ToolFeatureCard><h3>Product Information</h3><p>Scan product barcodes to look up prices, reviews, or specifications online. Useful for price comparison shopping or verifying product authenticity.</p></ToolFeatureCard>
                <ToolFeatureCard><h3>Inventory Management</h3><p>Scan multiple product codes for inventory tracking. Upload photos of barcodes to extract product IDs, SKUs, or serial numbers for database entry.</p></ToolFeatureCard>
                <ToolFeatureCard><h3>QR Code Reading</h3><p>Decode QR codes from screenshots, posters, or digital displays. Extract URLs, contact information, WiFi credentials, or other encoded data.</p></ToolFeatureCard>
                <ToolFeatureCard><h3>Document Processing</h3><p>Extract tracking numbers from shipping labels, scan receipts for expense tracking, or decode barcodes from scanned documents for data entry automation.</p></ToolFeatureCard>
            </ToolSection>

            <ToolSection variant="light" title="Frequently Asked Questions" grid="none">
                <div className="flex flex-col gap-[20px] mb-[32px]">
                    <ToolFeatureCard variant="faq"><h3>What barcode formats are supported?</h3><p>We support all major formats: QR codes, Code 128, Code 39, EAN-13, EAN-8, UPC-A, UPC-E, Codabar, ITF, Data Matrix, PDF417, Aztec Code, and MaxiCode. The tool automatically detects the format.</p></ToolFeatureCard>
                    <ToolFeatureCard variant="faq"><h3>Can I scan multiple barcodes at once?</h3><p>Yes! Upload an image containing multiple barcodes and our tool will detect and decode all of them. Results show each barcode type and data separately.</p></ToolFeatureCard>
                    <ToolFeatureCard variant="faq"><h3>What if my barcode image is blurry?</h3><p>Our scanner includes advanced image preprocessing that handles blurry images, poor lighting, and various angles. For best results, ensure the barcode is clearly visible and well-lit in your image.</p></ToolFeatureCard>
                    <ToolFeatureCard variant="faq"><h3>Is my data secure when scanning?</h3><p>Yes. Images are uploaded securely via HTTPS, processed on our servers, and immediately deleted after scanning. We never store your images or the decoded data.</p></ToolFeatureCard>
                </div>
                <ToolFaqCta prompt="More questions?" to="/faq" label="Visit our FAQ page" />
            </ToolSection>

            <ToolSection variant="muted" title="Related Scanning &amp; Extraction Tools" grid="related">
                <ToolRelatedCard to="/tools/qr-code-generator" icon={<QrCode size={24} />} title="QR Code Generator" description="Create custom QR codes with colors, logos, and styles" linkLabel="Try Tool" />
                <ToolRelatedCard to="/tools/scan-image-to-text" icon={<Upload size={24} />} title="OCR Text Extractor" description="Extract text from images and scanned PDFs" linkLabel="Try Tool" />
                <ToolRelatedCard to="/tools/compare-text" icon={<Upload size={24} />} title="Text Comparison" description="Compare extracted barcode data or text files" linkLabel="Try Tool" />
            </ToolSection>
        </>
    );
};

export default BarcodeReaderPage;
